import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Hidden,
  Backdrop,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { downloadCustomUploadedFile } from "../../../../../containers/actions/common/fileActions";
import { fetchDSBatchDetails } from "../../../../../containers/actions/common/saslAction";
import GetAppIcon from "@material-ui/icons/GetApp";
import DescriptionIcon from "@material-ui/icons/Description";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CloseIcon from "@material-ui/icons/Close";
import StructureIcon from "@material-ui/icons/AccountTree";
import CommonDigitizedDocument from "../../../Digitisation/CommonDigitizedDocument";
import "./ConsumptionDetails.css";

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: "8px",
    transition: "all 0.3s ease",
    // display: "flex",
    // alignItems: "center",
    padding: theme.spacing(2),
    minHeight: "120px",
  },
  documentPreview: {
    width: "120px",
    height: "130px",
    backgroundColor: "#ffffff",
    border: "2px solid #e0e0e0",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    position: "relative",
    flexShrink: 0,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  documentIcon: {
    color: "#1391C9",
    fontSize: "2rem",
    marginBottom: theme.spacing(0.5),
  },
  previewText: {
    fontSize: "0.6rem",
    textAlign: "center",
    color: "#666",
    fontWeight: "bold",
    textTransform: "uppercase",
    lineHeight: "1.5",
    maxWidth: "100px",
    wordWrap: "break-word",
    hyphens: "auto",
  },
  contentArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  headerSection: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: '0px',
    paddingTop: '10px',
    position: 'relative',
    flexDirection: 'column'
  },
  titleSection: {
    flex: 1,
  },
  documentTitle: {
    fontWeight: 500,
    fontSize: "16px",
    marginBottom: "2px",
    marginTop: theme.spacing(1.5),
    textTransform: "none",
  },
  fileName: {
    fontSize: "0.85rem",
    color: "#666",
    marginBottom: theme.spacing(1),
  },
  downloadButton: {
    color: "#1391C9",
    padding: "4px",
    "&:hover": {
      backgroundColor: "rgba(19, 145, 201, 0.1)",
    },
  },
  reviewSection: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  reviewChip: {
    height: "20px",
    fontSize: "0.7rem",
    fontWeight: "bold",
  },
  reviewedChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  pendingChip: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  detailsSection: {
    display: "flex",
    gap: theme.spacing(3),
    flexWrap: "wrap",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: "#666",
    fontWeight: "bold",
    marginBottom: "2px",
  },
  detailValue: {
    fontSize: "0.85rem",
    color: "#333",
  },
  noDataMessage: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: "#333",
  },
  titleGrid: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    backgroundColor: "#f5f9ff",
    width: "100%",
  },
  closeText: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: 500,
    "&:hover": {
      borderBottom: "2px solid red",
    },
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    cursor: "pointer",
    padding: "0.3rem",
  },
  title: {
    fontWeight: 500,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  main: {
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
  },
  clickableCard: {
    cursor: "pointer",
    // "&:hover": {
    //   boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
    //   transform: "translateY(-2px)",
    // },
  },
  link: {
    cursor: "pointer",
    color: "blue",
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));

const ConsumptionDetailsCards = ({ batchDetails: initialBatchDetails, loading, cmoBatchNumber, onDataRefresh }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [batchDetails, setBatchDetails] = useState(initialBatchDetails);

  useEffect(() => {
    if (initialBatchDetails) {
      setBatchDetails(initialBatchDetails);
    }
  }, [initialBatchDetails]);

  // State for modal dialog
  const [viewDigitizedDocument, setViewDigitizedDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [parsedKvpData, setParsedKvpData] = useState(null);
  const [kvpParseError, setKvpParseError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const digitizedDocHandleCloseRef = React.useRef(null);

  // Helper function to get the appropriate KVP data (edited or original)
  const getKvpData = (document) => {
    // Check if kvpJsonEdited exists and has content
    if (document.kvpJsonEdited && 
        typeof document.kvpJsonEdited === 'object' && 
        Object.keys(document.kvpJsonEdited).length > 0) {
      return document.kvpJsonEdited;
    }
    // Fall back to kvpJsonOriginal
    return document.kvpJsonOriginal;
  };

  useEffect(() => {
    if (selectedDocument && batchDetails?.digitizedExtractedData) {
      const updatedDocument = batchDetails.digitizedExtractedData.find(
        (doc) => doc.schrockenCustomFileID === selectedDocument.schrockenCustomFileID
      );
      if (updatedDocument) {
        
        setSelectedDocument(updatedDocument);
      }
    }
  }, [batchDetails]);

  // Parse kvpJson when selectedDocument changes
  useEffect(() => {
    if (selectedDocument) {
      const kvpData = getKvpData(selectedDocument);
      if (kvpData) {
        try {
          setKvpParseError(null);
          setParsedKvpData(kvpData);
        } catch (error) {
          console.error("Error parsing kvpJson:", error);
          setKvpParseError(error.message);
          setParsedKvpData(kvpData || null);
        }
      } else {
        setParsedKvpData(null);
        setKvpParseError(null);
      }
    } else {
      setParsedKvpData(null);
      setKvpParseError(null);
    }
  }, [selectedDocument]);

  // Filter documents that have consumption_batch_number or total_weight and get latest for each type
  const filterRelevantDocuments = (digitizedData) => {
    if (!digitizedData || !Array.isArray(digitizedData)) return [];

    // First, filter documents that have consumption_batch_number or total_weight
    const relevantDocs = digitizedData.filter((item) => {
      const kvpData = getKvpData(item);
      if (!kvpData) return false;

      // Check if document has consumption_batch_number (in bowls structure)
      const hasConsumptionBatchNumber =
        kvpData.bowls &&
        Array.isArray(kvpData.bowls) &&
        kvpData.bowls.some(
          (bowl) =>
            bowl.rows &&
            Array.isArray(bowl.rows) &&
            bowl.rows.some((row) => row.consumption_batch_number)
        );

      // Check if document has total_weight
      const hasTotalWeight = kvpData.total_weight && Object.keys(kvpData.total_weight).length > 0

      return hasConsumptionBatchNumber || hasTotalWeight;
    });

    // Group by document type and get the latest for each type
    const docTypeMap = new Map();

    relevantDocs.forEach((doc) => {
      const kvpData = getKvpData(doc);
      const docType = kvpData?.document_type;
      if (!docType) return;

      const existing = docTypeMap.get(docType);
      if (!existing) {
        docTypeMap.set(docType, doc);
      } else {
        // Compare dates - assume newer schrockenCustomFileID means more recent
        // or compare by filename timestamp if available
        const currentId = doc.schrockenCustomFileID;
        const existingId = existing.schrockenCustomFileID;

        if (currentId > existingId) {
          docTypeMap.set(docType, doc);
        }
      }
    });

    return Array.from(docTypeMap.values());
  };

  // // Filter documents based on review flags and document category
  // const filterRelevantDocuments = (digitizedData) => {
  //   if (!digitizedData || !Array.isArray(digitizedData)) return [];

  //   // Filter documents based on category and review flags
  //   const relevantDocs = digitizedData.filter((item) => {
  //     const kvpData = getKvpData(item);
  //     if (!kvpData) return false;

  //     // Find the document in customFilesList using schrockenCustomFileID
  //     const customFile = batchDetails?.customFilesList?.find(
  //       file => file.schrockenCustomFileID === item.schrockenCustomFileID?.toString()
  //     );

  //     if (!customFile) return false;

  //     const category = customFile.documentCategoryType;

  //     // Check if the document category is enabled based on review flags
  //     if (category === "Post-Manufacturing" && batchDetails?.reviewPostMFGFieldsReqd === true) {
  //       return true;
  //     }
  //     // if (category === "Post-Shipment Documents" && batchDetails?.reviewPostShipmentFieldsReqd === true) {
  //     //   return true;
  //     // }
  //     // if (category === "Pre-Shipment Documents" && batchDetails?.reviewPreShipmentFieldsReqd === true) {
  //     //   return true;
  //     // }

  //     return false;
  //   });

  //   // Group by document type and get the latest for each type
  //   const docTypeMap = new Map();

  //   relevantDocs.forEach((doc) => {
  //     const kvpData = getKvpData(doc);
  //     const docType = kvpData?.document_type;
  //     if (!docType) return;

  //     const existing = docTypeMap.get(docType);
  //     if (!existing) {
  //       docTypeMap.set(docType, doc);
  //     } else {
  //       // Compare dates - assume newer schrockenCustomFileID means more recent
  //       // or compare by filename timestamp if available
  //       const currentId = doc.schrockenCustomFileID;
  //       const existingId = existing.schrockenCustomFileID;

  //       if (currentId > existingId) {
  //         docTypeMap.set(docType, doc);
  //       }
  //     }
  //   });

  //   return Array.from(docTypeMap.values());
  // };

  const getDocumentDisplayName = (documentType) => {
    if (!documentType) return "Unknown Document";
    const name = String(documentType)
      .trim()
      .replace(/([A-Z])/g, " $1")
      .trim();
    const titled = name.replace(
      /\b\w+/g,
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    );
    return titled.replace(/\bOf\b/g, "of");
  };

  const handleDownload = (document) => {
    // Use the schrockenCustomFileID from the document to download via Redux action
    if (document.schrockenCustomFileID) {
      const data = { schrockenCustomFileID: document.schrockenCustomFileID };
      dispatch(downloadCustomUploadedFile(data));
    } else {
      // Fallback: try to get schrockenCustomFileID from kvpJsonOriginal if available
      const kvpData = getKvpData(document);
      if (kvpData?.filename) {
        alert(
          `Download not available - missing file ID for: ${kvpData.filename}`
        );
      } else {
        alert("Document download not available - missing file information");
      }
    }
  };

  const handleOpenDigitizedDocument = (document) => {
    setSelectedDocument(document);
    setViewDigitizedDocument(true);
  };

  const handleCloseDigitizedDocument = async () => {
    if (digitizedDocHandleCloseRef.current) {
      digitizedDocHandleCloseRef.current();
    } else {
      setViewDigitizedDocument(false);
      setSelectedDocument(null);
      setParsedKvpData(null);
      setKvpParseError(null);
      await handleDataRefresh();
    }
  };

  const handleDataRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (batchDetails?.schrockenDeliveryScheduleBatchID) {
        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDeliveryScheduleBatchDetails`,
          { schrockenDeliveryScheduleBatchID: batchDetails.schrockenDeliveryScheduleBatchID }
        );
        
        if (response.data.status && response.data.data) {
          
          setBatchDetails(response.data.data);
          
          if (onDataRefresh) {
            await onDataRefresh();
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate relevant documents - must be called before any conditional returns
  const relevantDocuments = useMemo(() => {
    const docs = filterRelevantDocuments(batchDetails?.digitizedExtractedData);
    
    return docs;
  }, [batchDetails?.digitizedExtractedData]);

  const renderDigitizedComponent = () => {
    if (!selectedDocument) return null;

    if (kvpParseError) {
      return (
        <Box p={3}>
          <Typography variant="h6" color="error">
            Error Processing Document
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
            Failed to parse document data
            {/* : {kvpParseError} */}
          </Typography>
          {selectedDocument?.kvpJsonOriginal && (
            <>
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Raw document data:
              </Typography>
              <pre
                style={{
                  marginTop: 8,
                  padding: 16,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  fontSize: "12px",
                }}
              >
                {JSON.stringify(getKvpData(selectedDocument), null, 2)}
              </pre>
            </>
          )}
        </Box>
      );
    }

    if (!parsedKvpData) {
      return (
        <Box p={3}>
          <Typography variant="h6">Loading Document...</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 16 }}
          >
            Processing digitized document data...
          </Typography>
        </Box>
      );
    }

    // Document type validation commented out - let CommonDigitizedDocument handle all types
    // const supportedDocumentTypes = [
    //   // New format with spaces
    //   "Bill Of Lading",
    //   "Commercial Invoice", 
    //   "Certificate Of Analysis",
    //   "Certificate Of Manufacture",
    //   "Packing Slip",
    //   "Consumption Details",
    //   "Delivery Note",
    //   "Packing List",
    //   "Confirmation Of Partial Manufacturing Of An Medicinal Product",
    //   "Finished Product Dispatch CheckList",
    //   // Legacy camelCase format (for backward compatibility)
    //   "CertificateOfAnalysis",
    //   "CertificateOfManufacture",
    //   "CommercialInvoice",
    //   "BillOfLading",
    //   "PackingSlip",
    //   "ConsumptionDetails",
    //   "DeliveryNote",
    //   "PackingList",
    //   "ConfirmationOfPartialManufacturingOfAnMedicinalProduct",
    //   "FinishedProductDispatchCheckList",
    // ];

    const documentType = selectedDocument.kvpJsonOriginal?.document_type || getKvpData(selectedDocument)?.document_type;

    // Document type validation removed - CommonDigitizedDocument will handle all document types
    // if (!supportedDocumentTypes.includes(documentType)) {
    //   return (
    //     <Box p={3}>
    //       <Typography variant="h6">Unsupported Document Type</Typography>
    //       <Typography
    //         variant="body2"
    //         color="textSecondary"
    //         style={{ marginTop: 16 }}
    //       >
    //         Document type "{documentType || "Unknown"}" is not supported. Raw
    //         data:
    //       </Typography>
    //       <pre
    //         style={{
    //           marginTop: 16,
    //           padding: 16,
    //           backgroundColor: "#f5f5f5",
    //           borderRadius: 4,
    //           fontSize: "12px",
    //         }}
    //       >
    //         {JSON.stringify(parsedKvpData, null, 2)}
    //       </pre>
    //     </Box>
    //   );
    // }

    return (
      <CommonDigitizedDocument
        initialData={parsedKvpData}
        onClose={async () => {
          setViewDigitizedDocument(false);
          setSelectedDocument(null);
          setParsedKvpData(null);
          setKvpParseError(null);
          await handleDataRefresh();
        }}
        handleCloseRef={digitizedDocHandleCloseRef}
        schrockenCustomFileID={selectedDocument?.schrockenCustomFileID}
        digitizedData={{
          ...selectedDocument,
          documentType: documentType,
          kvpJson: JSON.stringify(parsedKvpData),
        }}
        batchDetails={batchDetails}
        poNumber={batchDetails?.scheduleAgreementNumber}
        batchNumber={batchDetails?.vendorBatchNumber}
        cmoBatchNumber={cmoBatchNumber}
        // onDataRefresh={handleDataRefresh}
      />
    );
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography style={{ marginLeft: 16 }}>
          Loading consumption details...
        </Typography>
      </div>
    );
  }

  if (relevantDocuments.length === 0) {
    return (
      <div className={classes.noDataMessage}>
        <DescriptionIcon
          style={{ fontSize: 48, marginBottom: 16, color: "#ccc" }}
        />
        <Typography variant="h6" gutterBottom>
          No Consumption Details Found
        </Typography>
        <Typography variant="body2">
          No documents are available.
        </Typography>
      </div>
    );
  }

  return (
    <Box>
      <Backdrop
        open={isRefreshing}
        style={{ zIndex: 9999, color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      >
        <div style={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" style={{ marginTop: 16, fontWeight: 500 }}>
            Refreshing data...
          </Typography>
        </div>
      </Backdrop>

      <div className="display-cards-wrapper">
        {relevantDocuments.map((document, index) => {
          const kvpData = getKvpData(document);
          const documentType = kvpData?.document_type || "";
          const displayName = getDocumentDisplayName(documentType);
          const fieldsToReview = kvpData?.noOfFieldsToBeReviewed || 0;
          const fieldsReviewed = kvpData?.noOfFieldsReviewed || 0;
          const fileName = kvpData?.filename || "Unknown file";


          return (
            <Card
              elevation={0}
              key={`${document.schrockenCustomFileID}-${fieldsToReview}-${fieldsReviewed}`}
              className={`${classes.card} document-card`}
              
            >
              <div style={{flexGrow: 1}} onClick={() => handleOpenDigitizedDocument(document)}>
                {/* Document Preview Thumbnail */}
                <div className={classes.documentPreview}>
                  <InsertDriveFileIcon className={classes.documentIcon} />
                  <Typography className={classes.previewText}>
                    {getDocumentDisplayName(documentType)}
                  </Typography>
                </div>

                {/* Content Area */}
                <div className={classes.contentArea}>
                  {/* Header Section */}
                  <div className={classes.headerSection}>
                    <div className={classes.titleSection}>
                      <Typography className={classes.documentTitle}>
                        {displayName}
                      </Typography>
                    </div>

                    
                  </div>
                  

                  <div className="fields-review-status">
                    <Typography variant="body2" style={{ margin: "0.5rem 0" }}>
                      No. of fields that require review - {fieldsToReview || 0}
                    </Typography>
                    <Typography variant="body2" style={{ margin: "0.5rem 0" }}>
                      No. of fields reviewed - {fieldsReviewed || 0}
                    </Typography>
                    {(fieldsToReview > 0 || fieldsReviewed > 0) && 
                     !(document.kvpJsonOriginal?.noOfFieldsReviewed === 0 && document.kvpJsonOriginal?.noOfFieldsToBeReviewed === 0) && (
                      <Typography variant="body2" >
                        Review marked as complete - {kvpData?.markAsReviewed ? "Yes" : "No"}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
              <div className="download-doc-styles">
                    <Grid
                item
                xs={12}
                style={{ display: "flex", alignItems: "center" }}
                className="download-link"
                onClick={() => handleDownload(document)}
              >
                <GetAppIcon fontSize="small" style={{ color: "blue" }} />
                <Typography
                  variant={"body2"}
                  className={classes.link}
                  style={{
                    textDecoration: "underline",
                  }}
                >
                  {displayName}
                </Typography>
              </Grid>
                  </div>

              
            </Card>
          );
        })}
      </div>

      {/* Modal Dialog for Digitized Document */}
      {viewDigitizedDocument && selectedDocument && (
        <Dialog
          fullScreen
          open={viewDigitizedDocument}
          onClose={handleCloseDigitizedDocument}
        >
          <DialogTitle disableTypography style={{ backgroundColor: "#f5f9ff" }}>
            <Grid
              container
              justifyContent="center"
              className={classes.titleGrid}
            >
              <Grid item xs={11} sm={11} md={11} className={classes.main}>
                <Typography variant="h4" className={classes.title}>
                  {getDocumentDisplayName(
                    getKvpData(selectedDocument)?.document_type
                  )}
                </Typography>
              </Grid>
              <Grid item xs={1} sm={1} md={1}>
                <Hidden xsDown implementation="css">
                  <Typography
                    variant="body2"
                    onClick={handleCloseDigitizedDocument}
                    className={classes.closeText}
                  >
                    Cancel{" "}
                    <CloseIcon
                      fontSize="large"
                      color="error"
                      className={classes.icon}
                    />
                  </Typography>
                </Hidden>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Box>{renderDigitizedComponent()}</Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default ConsumptionDetailsCards;
